export type InputFormat = 'free-text' | 'json' | 'comma-separated';

/**
 * Parse free-text input (newline-separated)
 */
export function parseFreeText(input: string): string[] {
  return input
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * Parse JSON array input
 */
export function parseJSON(input: string): string[] {
  try {
    const parsed = JSON.parse(input);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0);
    }
    throw new Error('Input must be a JSON array');
  } catch (error) {
    throw new Error(`Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Parse comma-separated input
 */
export function parseCommaSeparated(input: string): string[] {
  return input
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/**
 * Auto-detect input format
 */
export function detectFormat(input: string): InputFormat {
  const trimmed = input.trim();
  
  // Check for JSON array
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      // Not valid JSON, continue
    }
  }
  
  // Check for comma-separated (more commas than newlines)
  const commaCount = (trimmed.match(/,/g) || []).length;
  const newlineCount = (trimmed.match(/\n/g) || []).length;
  
  if (commaCount > newlineCount && commaCount > 0) {
    return 'comma-separated';
  }
  
  // Default to free-text
  return 'free-text';
}

/**
 * Parse input using specified format
 */
export function parseInput(input: string, format: InputFormat): string[] {
  switch (format) {
    case 'json':
      return parseJSON(input);
    case 'comma-separated':
      return parseCommaSeparated(input);
    case 'free-text':
    default:
      return parseFreeText(input);
  }
}

/**
 * Clean and deduplicate items (case-insensitive)
 */
export function cleanItems(items: string[]): string[] {
  const seen = new Set<string>();
  const cleaned: string[] = [];
  
  for (const item of items) {
    const lower = item.toLowerCase();
    if (!seen.has(lower)) {
      seen.add(lower);
      cleaned.push(item);
    }
  }
  
  return cleaned;
}


